import { Country, ExtraTempInfoInputType, Maybe, TemporaryData } from '../../gql/generated/graphql';

export const AFFILIATE_PARAMS = {
  AFFILIATE_EXTERNAL_ID: 'affiliate_external_id',
};

export const SHOPPING_CART_PARAMS = {
  PAYOFF: 'payoff',
  CREDIT_SCORE: 'credit_score',
  DOWN_PAYMENT: 'down_payment',
  TERM: 'term',
};

const getShoppingCart = (search: string) => {
  const params = new URLSearchParams(search);
  const payoff = params.get(SHOPPING_CART_PARAMS.PAYOFF);
  const creditScore = params.get(SHOPPING_CART_PARAMS.CREDIT_SCORE);
  const downPayment = params.get(SHOPPING_CART_PARAMS.DOWN_PAYMENT);
  const term = params.get(SHOPPING_CART_PARAMS.TERM);

  if (!payoff && !creditScore && !downPayment && !term) {
    return undefined;
  }

  return {
    buyout: payoff ? Number(payoff) : undefined,
    credit_score: creditScore ? Number(creditScore) : undefined,
    down_payment: downPayment ? Number(downPayment) : undefined,
    loan_term: term ? Number(term) : undefined,
  };
};

// TODO-JK:: add/test zip and shopping cart
export const FLOW_ENTRY_PARAMS = {
  ZIP: 'zip',
  FIRST_NAME: 'first_name',
  LAST_NAME: 'last_name',
  VIN: 'vin',
  LICENSE_PLATE: 'license_plate',
  STATE: 'state',
  CONTACT_US: 'contact_us',
  REFERRAL_CODE: 'rf',
  REFERRAL_EMAIL: 'rfem',
  REFERRAL_PHONE: 'rfph',
  REFERRAL_NAME: 'rfnm',
  REFERRAL_CLOSER: 'rfcid',
  REFERRER_ID: 'rid',
  REFERRER_FN: 'rfn',
  METHODFI_VARIATION: 'methodfi_variation',
  MARKETPLACE: 'mp',
  MARKETPLACE_TYPE: 'mpt',
};

export const isSellerEntry = (search: Maybe<string>) => {
  const params = new URLSearchParams(search ?? '');
  return (
    params.get(FLOW_ENTRY_PARAMS.MARKETPLACE) === 'true' &&
    params.get(FLOW_ENTRY_PARAMS.MARKETPLACE_TYPE) === 'seller'
  );
};

// Sell-page drivers only see the selector after switching to buyout, so they start on Keep.
export const getInitialWantsToSell = (
  data?: Maybe<Pick<TemporaryData, 'wants_to_sell' | 'query_params'>>,
) => (data?.wants_to_sell === true && !isSellerEntry(data?.query_params)).toString();

export const getFlowParams = (search: string) => {
  const params = new URLSearchParams(search);
  const vin = params.get(FLOW_ENTRY_PARAMS.VIN) || undefined;
  const referralCode = params.get(FLOW_ENTRY_PARAMS.REFERRAL_CODE) || undefined;
  const referralEmail = params.get(FLOW_ENTRY_PARAMS.REFERRAL_EMAIL) || undefined;
  const referralPhone = params.get(FLOW_ENTRY_PARAMS.REFERRAL_PHONE) || undefined;
  const referralName = params.get(FLOW_ENTRY_PARAMS.REFERRAL_NAME) || undefined;
  const referralCloser = params.get(FLOW_ENTRY_PARAMS.REFERRAL_CLOSER) || undefined;
  const referrerIdStr = params.get(FLOW_ENTRY_PARAMS.REFERRER_ID) || undefined;
  const referrerId =
    referrerIdStr && /^\d+$/.test(referrerIdStr) ? Number(referrerIdStr) : undefined;
  const referrerFn = params.get(FLOW_ENTRY_PARAMS.REFERRER_FN) || undefined;
  const licensePlate = params.get(FLOW_ENTRY_PARAMS.LICENSE_PLATE) || undefined;
  const licensePlateState = params.get(FLOW_ENTRY_PARAMS.STATE) || undefined;
  const zip = params.get(FLOW_ENTRY_PARAMS.ZIP) || undefined;
  const firstName = params.get(FLOW_ENTRY_PARAMS.FIRST_NAME) || undefined;
  const lastName = params.get(FLOW_ENTRY_PARAMS.LAST_NAME) || undefined;
  const contact_us = params.get(FLOW_ENTRY_PARAMS.CONTACT_US) || undefined;
  const methodFiVariationStr = params.get(FLOW_ENTRY_PARAMS.METHODFI_VARIATION) || undefined;
  const methodFiVariation =
    methodFiVariationStr && /^\d+$/.test(methodFiVariationStr)
      ? Number(methodFiVariationStr)
      : undefined;

  return {
    vin,
    licensePlate,
    licensePlateState,
    zip,
    firstName,
    lastName,
    contact_us,
    shoppingCart: getShoppingCart(search),
    referralCode,
    referralEmail,
    referralPhone,
    referralName,
    referralCloser,
    referrerId,
    referrerFn,
    methodFiVariation,
  };
};

export type FlowParams = ReturnType<typeof getFlowParams>;
export type ReferralParams = {
  referrer_id: number | null;
  code: string | null;
};

export interface PrequalParams {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  phone: string;
  prequalId: string;
}

export const getExtraInfo = (
  country: Country,
  flowParams?: FlowParams,
  prequalParams?: PrequalParams | null,
  wantsToSell?: boolean,
) => {
  let extraInfo: ExtraTempInfoInputType = {
    country,
    wants_to_sell: wantsToSell || undefined,
    zip: flowParams?.zip,
    first_name: flowParams?.firstName?.trim(),
    last_name: flowParams?.lastName?.trim(),
    shopping_cart: flowParams?.shoppingCart,
  };

  if (prequalParams) {
    extraInfo = {
      ...extraInfo,
      first_name: prequalParams.firstName?.trim(),
      last_name: prequalParams.lastName?.trim(),
      address_line: prequalParams.address,
      city: prequalParams.city,
      state: prequalParams.state,
      zip: prequalParams.zip,
      email: prequalParams.email,
      phone_number: prequalParams.phone,
      prequal_id: prequalParams.prequalId,
    };
  }

  return extraInfo;
};
